import React, { createContext, useCallback, useMemo, useState } from 'react';
import { FeedbackSubmissionStatus } from '../../types/feedback/FeedbackResponse';

interface IDesignFeedbackGroupResponseContext {
  isFeedbackSubmitted: boolean;
  setIsFeedbackSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  selectedDesignOption: object | null;
  selectedDesignOptionId: string | null;
  selectedDesignOptionPreviews: string[] | null;
  selectedStatus: FeedbackSubmissionStatus | null;
  requestedChanges: string | null;
  isApproved: boolean;
  isRejected: boolean;
  setRequestedChanges: any;
  approveDesignOption: object;
  rejectDesignOptions: object;
  resetCustomerFeedback: any;
}

const DesignFeedbackGroupResponseContext =
  createContext<IDesignFeedbackGroupResponseContext | null>(null);

type CustomerFeedback = {
  selectedStatus: FeedbackSubmissionStatus | null;
  selectedDesignOption: object;
  selectedDesignOptionId: string;
  selectedDesignOptionPreviews: string[];
  requestedChanges: string;
};

const defaultCustomerFeedback: CustomerFeedback = {
  selectedStatus: null, // FeedbackSubmissionStatus
  selectedDesignOption: {},
  selectedDesignOptionId: '',
  selectedDesignOptionPreviews: [],
  requestedChanges: '',
};

type DesignFeedbackGroupResponseProviderProps = {
  children: React.ReactNode;
};

const DesignFeedbackGroupResponseProvider = ({
  children,
}: DesignFeedbackGroupResponseProviderProps) => {
  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);

  const [customerFeedback, setCustomerFeedback] = useState(
    defaultCustomerFeedback
  );

  const resetCustomerFeedback = () => {
    setCustomerFeedback(defaultCustomerFeedback);
  };

  const approveDesignOption = (option: { optionId: string; previews: any }) => {
    setCustomerFeedback({
      selectedStatus: FeedbackSubmissionStatus.APPROVED,
      selectedDesignOption: option,
      selectedDesignOptionId: option.optionId,
      selectedDesignOptionPreviews: option.previews,
      requestedChanges: '',
    });
  };

  const rejectDesignOptions = () => {
    setCustomerFeedback({
      selectedStatus: FeedbackSubmissionStatus.REJECTED,
      selectedDesignOption: {},
      selectedDesignOptionId: '',
      selectedDesignOptionPreviews: [],
      requestedChanges: '',
    });
  };

  const setRequestedChanges = useCallback(
    (changes: string) => {
      setCustomerFeedback({
        ...customerFeedback,
        requestedChanges: changes,
      });
    },
    [customerFeedback]
  );

  const {
    selectedStatus,
    selectedDesignOption,
    selectedDesignOptionId,
    selectedDesignOptionPreviews,
    requestedChanges,
  } = customerFeedback;

  const isApproved = selectedStatus === FeedbackSubmissionStatus.APPROVED;
  const isRejected = selectedStatus === FeedbackSubmissionStatus.REJECTED;

  const contextValues = useMemo<IDesignFeedbackGroupResponseContext>(
    () => ({
      isFeedbackSubmitted,
      setIsFeedbackSubmitted,
      selectedDesignOption,
      selectedDesignOptionId,
      selectedDesignOptionPreviews,
      selectedStatus,
      requestedChanges,
      isApproved,
      isRejected,
      setRequestedChanges,
      approveDesignOption,
      rejectDesignOptions,
      resetCustomerFeedback,
    }),
    [
      isFeedbackSubmitted,
      isApproved,
      isRejected,
      requestedChanges,
      selectedDesignOption,
      selectedDesignOptionId,
      selectedDesignOptionPreviews,
      selectedStatus,
      setRequestedChanges,
    ]
  );

  return (
    <DesignFeedbackGroupResponseContext.Provider value={contextValues}>
      {children}
    </DesignFeedbackGroupResponseContext.Provider>
  );
};

export {
  DesignFeedbackGroupResponseContext,
  DesignFeedbackGroupResponseProvider,
};
